<template>
  <FullScreenModal :open="open" content-class="max-w-screen-2xl" @close="close">
    <div v-if="reconfiguringHandling" data-test="handling-type-step">
      <h2 class="text-lg font-semibold mb-4 text-center w-full">{{ t("handling_type_title") }}</h2>
      <HandlingTypeForm
        :claim="currentClaim"
        :default-values="{ handlingType: 'replacement', claimHandler: 'merchant' }"
        @submitted="close"
      />
    </div>
    <div v-else-if="invoiceApprovalRequired || quotationApprovalRequired">
      <h1 class="text-2xl font-bold">{{ invoiceApprovalRequired ? t("invoice_title") : t("quotation_title") }}</h1>

      <div class="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div class="lg:sticky lg:top-4 lg:self-start">
          <AiAnalysisDocumentPreview :documents="previewDocuments" />
        </div>

        <div class="space-y-6">
          <ApprovalContextPanel
            :claim="currentClaim"
            :document="approvalDocument"
            :analysable-type="invoiceApprovalRequired ? 'Claims::ClaimInvoice' : 'Claims::Quotation'"
            :declaration="declaration"
            :quotation="validatedQuotation"
          />

          <NewProductForm
            v-if="replacementProductRequired"
            data-test="replacement-product-form"
            v-model="replacementProduct"
            :price-band-max="currentClaim?.priceBandMax"
            @update:valid="replacementProductValid = $event"
          />

          <ApprovalRefusalForm
            v-if="showRefusalForm"
            data-test="refusal-form"
            v-model:reason="refusalReason"
            v-model:refusal-type="refusalType"
            :show-refusal-type="quotationApprovalRequired"
          />
        </div>
      </div>

      <div class="sticky bottom-0 z-10 mt-8 flex justify-end gap-2 border-t border-gray-200 bg-white py-4">
        <template v-if="invoiceApprovalRequired">
          <GenericButton
            default-style="bg-green-600 hover:bg-green-700 border-transparent text-white"
            data-test="approve-invoice-button"
            :disabled="submitting || (replacementProductRequired && !replacementProductValid)"
            @click="handleApproveInvoice"
          >
            {{ t("approve_invoice_button") }}
          </GenericButton>
          <GenericButton
            default-style="bg-red-600 hover:bg-red-700 border-transparent text-white"
            data-test="refuse-invoice-button"
            :disabled="submitting"
            @click="handleRefuseInvoice"
          >
            {{ t("refuse_invoice_button") }}
          </GenericButton>
        </template>
        <template v-else-if="refusing">
          <SecondaryButton data-test="cancel-refusal-button" @click="cancelRefusal">
            {{ t("cancel_button") }}
          </SecondaryButton>
          <GenericButton
            default-style="bg-red-600 hover:bg-red-700 border-transparent text-white"
            data-test="confirm-refusal-button"
            :disabled="submitting"
            @click="handleRefuseQuotation"
          >
            {{ t("confirm_refusal_button") }}
          </GenericButton>
        </template>
        <template v-else>
          <GenericButton
            default-style="bg-green-600 hover:bg-green-700 border-transparent text-white"
            data-test="approve-quotation-button"
            :disabled="submitting"
            @click="handleApproveQuotation"
          >
            {{ t("approve_quotation_button") }}
          </GenericButton>
          <GenericButton
            default-style="bg-red-600 hover:bg-red-700 border-transparent text-white"
            data-test="refuse-quotation-button"
            @click="startRefusal"
          >
            {{ t("refuse_quotation_button") }}
          </GenericButton>
        </template>
      </div>
    </div>
  </FullScreenModal>
</template>

<script>
import { FullScreenModal, GenericButton, SecondaryButton } from "@estaly/ui";
import { useI18n } from "vue-i18n";
import { mapActions, mapGetters, mapState } from "vuex";
import AiAnalysisDocumentPreview from "@/components/claim/details/ai-analysis-flow/AiAnalysisDocumentPreview.vue";
import ApprovalContextPanel from "@/components/claim/details/action/ApprovalContextPanel.vue";
import ApprovalRefusalForm from "@/components/claim/details/action/ApprovalRefusalForm.vue";
import HandlingTypeForm from "@/components/claim/details/action/HandlingTypeForm.vue";
import NewProductForm from "@/components/claim/details/action/NewProductForm.vue";
import { emptyReplacementProduct, replacementProductFromAnalysis } from "@/utils/replacement_capture";

export default {
  components: {
    AiAnalysisDocumentPreview,
    ApprovalContextPanel,
    ApprovalRefusalForm,
    FullScreenModal,
    GenericButton,
    HandlingTypeForm,
    NewProductForm,
    SecondaryButton,
  },

  props: {
    open: {
      type: Boolean,
      default: false,
    },
    declaration: {
      type: Object,
      default: null,
    },
  },

  emits: ["close"],

  setup() {
    const { t } = useI18n();

    return { t };
  },

  data() {
    return {
      refusing: false,
      refusalReason: "",
      refusalType: "",
      reconfiguringHandling: false,
      replacementProduct: emptyReplacementProduct(),
      replacementProductValid: false,
      submitting: false,
    };
  },

  computed: {
    ...mapState("claims", ["currentClaim"]),
    ...mapGetters("claims", [
      "quotationApprovalRequired",
      "invoiceApprovalRequired",
      "claimPending",
      "shouldCaptureReplacementProduct",
    ]),

    // Validé à l'approbation de la facture, pour que le sinistre puisse être résolu dans la foulée.
    replacementProductRequired() {
      return this.invoiceApprovalRequired && this.shouldCaptureReplacementProduct(this.currentClaim);
    },

    approvalDocument() {
      return this.invoiceApprovalRequired ? this.currentClaim?.invoice : this.currentClaim?.quotation;
    },

    // Pour une réparation, la facture se valide au regard du devis approuvé.
    validatedQuotation() {
      if (!this.invoiceApprovalRequired || this.currentClaim?.handlingType !== "repair") return null;

      const quotation = this.currentClaim?.quotation;
      return quotation?.approvalStatus === "approved" ? quotation : null;
    },

    previewDocuments() {
      if (!this.approvalDocument?.url) return [];

      return [{ name: this.approvalDocument.fileName, link: this.approvalDocument.url }];
    },

    // Le motif de refus d'une facture est saisi d'emblée ; celui d'un devis n'apparaît
    // qu'une fois le refus engagé, car il conditionne la suite à donner.
    showRefusalForm() {
      return this.invoiceApprovalRequired || this.refusing;
    },
  },

  watch: {
    open: {
      immediate: true,
      handler(open) {
        if (open) {
          this.replacementProduct = replacementProductFromAnalysis(
            this.currentClaim?.invoice?.replacementProductAnalysis,
          );
        }
      },
    },
  },

  methods: {
    ...mapActions("claims", ["approveQuotation", "refuseQuotation", "approveInvoice", "refuseInvoice"]),
    ...mapActions("notifications", ["notify"]),

    // Bloque les boutons de décision le temps de la requête, pour éviter les doubles soumissions.
    async submit(request) {
      this.submitting = true;
      const response = await request();
      this.submitting = false;
      return response;
    },

    async handleApproveQuotation() {
      await this.submit(() => this.approveQuotation());
    },

    startRefusal() {
      this.refusing = true;
    },

    cancelRefusal() {
      this.resetRefusalForm();
    },

    async handleRefuseQuotation() {
      const params = {
        refusalReason: this.refusalReason,
        refusalType: this.refusalType,
      };
      await this.submit(() => this.refuseQuotation(params));
      // Le sinistre rechargé est repassé en attente de prise en charge : on enchaîne sur sa configuration.
      if (params.refusalType === "reconfigure_handling" && this.claimPending) {
        this.reconfiguringHandling = true;
      }
    },

    async handleRefuseInvoice() {
      await this.submit(() => this.refuseInvoice({ refusalReason: this.refusalReason }));
    },

    async handleApproveInvoice() {
      const response = await this.submit(() =>
        this.approveInvoice({
          replacementProduct: this.replacementProductRequired ? this.replacementProduct : null,
        }),
      );
      if (response?.success) this.close();
    },

    resetRefusalForm() {
      this.refusing = false;
      this.refusalReason = "";
      this.refusalType = "";
      this.reconfiguringHandling = false;
    },

    close() {
      this.resetRefusalForm();
      this.$emit("close");
    },
  },
};
</script>

<i18n src="./ActionModal.i18n.json"></i18n>
